import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ActionSheetActionRadiusConstantType,
    ActionSheetHeaderOffsetConstantType,
    ActionSheetDividerConstantType,
    ActionSheetDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../../../types";
import {
    concentric
} from "../../../../helpers/theme/concentric";
import {
    getHitArea
} from "../../../../helpers/theme/control";
import {
    webStyle
} from "../../../../utils";

export const ACTION_SHEET_TITLE_VARIANT: keyof NCoreUIKit.Typography = "titleLargeSize";

export const ACTION_SHEET_DESCRIPTION_VARIANT: keyof NCoreUIKit.Typography = "bodyMediumSize";

export const getActionSheetIsDividerVisible = ({
    isPreviousHighlighted,
    isNextHighlighted
}: ActionSheetDividerConstantType): boolean => {
    return !isPreviousHighlighted && !isNextHighlighted;
};

export const getActionSheetHeaderOffset = ({
    controlHeights,
    touchTargets,
    pointerType,
    typography
}: ActionSheetHeaderOffsetConstantType): number => {
    const hitArea = getHitArea({
        visualSize: controlHeights.small,
        touchTargets,
        pointerType
    });

    return (typography[ACTION_SHEET_TITLE_VARIANT].lineHeight - hitArea.minHeight) / 2;
};

export const getActionSheetActionRadius = ({
    controlPaddings,
    controlHeights,
    typography,
    radiuses,
    borders,
    spaces
}: ActionSheetActionRadiusConstantType): number => {
    const titleOffset = (typography[ACTION_SHEET_TITLE_VARIANT].lineHeight - controlHeights.small) / 2;

    return concentric({
        inset: Math.max(
            borders.line + spaces.spacingLg - controlPaddings.small,
            borders.line + spaces.spacingSm * 2 + borders.indicator + spaces.spacingXs + titleOffset
        ),
        outer: radiuses.surface,
        role: radiuses.actions
    });
};

const stylesheet = StyleSheet.create({
    header: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    headerText: {
        flexDirection: "column",
        minWidth: 0,
        flex: 1,
        ...webStyle({
            outlineWidth: 0
        })
    },
    list: {
        flexDirection: "column"
    },
    footer: {
        flexDirection: "row",
        alignItems: "center"
    },
    footerButton: {
        flexShrink: 1,
        flexBasis: 0,
        flexGrow: 1
    },
    divider: {
        flexShrink: 0
    }
});

export const useStyles = ({
    controlPaddings,
    controlHeights,
    touchTargets,
    pointerType,
    typography,
    iconSizes,
    borders,
    colors,
    spaces
}: ActionSheetDynamicStyleType) => {
    const hitArea = getHitArea({
        visualSize: controlHeights.small,
        touchTargets,
        pointerType
    });

    const headerOffset = getActionSheetHeaderOffset({
        controlHeights,
        touchTargets,
        pointerType,
        typography
    });

    const dividerInset = spaces.spacingLg - spaces.spacingXs;

    const styles = {
        back: {
            marginStart: (iconSizes.small - hitArea.minWidth) / 2,
            marginTop: headerOffset
        } as Mutable<ViewStyle>,
        divider: {
            backgroundColor: colors.content.border.subtle,
            marginHorizontal: dividerInset,
            height: borders.separator
        } as Mutable<ViewStyle>,
        list: {
            paddingHorizontal: spaces.spacingXs,
            paddingBottom: spaces.spacingSm,
            paddingTop: spaces.spacingXs
        } as Mutable<ViewStyle>,
        action: {
            marginEnd: -controlPaddings.small,
            marginTop: headerOffset
        } as Mutable<ViewStyle>,
        hiddenDivider: {
            marginHorizontal: dividerInset,
            height: borders.separator
        } as Mutable<ViewStyle>,
        header: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        headerText: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        footer: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
